import { useState } from 'react'
import { COLUMNS, CUSTOM_COLUMN_PREFIX, PAGE_SIZES, type ColumnId, type PageSize } from '@/features/contacts/contactsTableSettings'

/** Columns and page size of a list's contacts table (localStorage 'pp.listTable'), shared by all lists. */
const STORAGE_KEY = 'pp.listTable'

export interface ListTableSettings {
  pageSize: PageSize
  /** Visible column ids in order; always starts with 'name'. Same ids as the Contacts table. */
  columns: ColumnId[]
}

export const DEFAULT_LIST_TABLE: ListTableSettings = {
  pageSize: 25,
  columns: ['name', 'email', 'mobile', 'owner', 'lastActivity'],
}

function load(): ListTableSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<ListTableSettings> | null
    if (!raw) return DEFAULT_LIST_TABLE
    const builtin = new Set(COLUMNS.map((c) => c.id))
    const kept = (Array.isArray(raw.columns) ? raw.columns : DEFAULT_LIST_TABLE.columns).filter(
      (id): id is string => typeof id === 'string' && (builtin.has(id) || id.startsWith(CUSTOM_COLUMN_PREFIX)) && id !== 'name',
    )
    const pageSize = (PAGE_SIZES as readonly number[]).includes(raw.pageSize as number) ? (raw.pageSize as PageSize) : 25
    return { pageSize, columns: ['name', ...new Set(kept)] }
  } catch {
    return DEFAULT_LIST_TABLE
  }
}

export function useListTableSettings(): [ListTableSettings, (next: ListTableSettings) => void] {
  const [settings, setSettings] = useState<ListTableSettings>(load)
  function update(next: ListTableSettings) {
    setSettings(next)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // storage unavailable — keep the choice for this visit
    }
  }
  return [settings, update]
}
